// Per-job rollup for the Top 10 Jobs overview.
// ----------------------------------------------------------------------------
// Pure data layer shared by TopJobsOverview (fetching + ranking) and
// TopJobsCharts (rendering). Kept free of React and recharts so the maths can
// be exercised on its own, and so the two component files do not have to import
// each other.
//
// Input is the /v1/forms/analytics feed with no `job_id`: every form for the
// scope, which is folded into one entry per job here.

// Mirrors the FORM_TYPES colours in jobanalytics.jsx. Duplicated rather than
// imported because jobanalytics.jsx imports this feature — sharing the constant
// the other way would make the modules circular.
export const FORM_TYPE_META = [
  { code: 'BBS', label: 'BBS Card', color: '#155DFC' },
  { code: 'SHP', label: 'Safety & Health', color: '#00A63E' },
  { code: 'WDS', label: 'Weekly Driver Log', color: '#E17100' },
  { code: 'AIF', label: 'Accident / Incident', color: '#E7000B' },
  { code: 'TRAILER', label: 'Trailer Inspection', color: '#7C3AED' },
];
export const FORM_CODES = FORM_TYPE_META.map((f) => f.code);

// The server ranks AIF classifications most-severe-first. Ten buckets is too
// many to stack legibly, so they roll up into four tiers here. `severity_hint`
// is null when the observer ticked no classification box at all.
export const SEVERITY_TIERS = [
  {
    key: 'serious',
    label: 'Serious',
    color: '#991B1B',
    matches: ['fatal', 'lost time', 'recordable'],
  },
  {
    key: 'moderate',
    label: 'Moderate',
    color: '#E7000B',
    matches: ['medical treatment', 'first aid', 'motor vehicle accident'],
  },
  {
    key: 'minor',
    label: 'Minor',
    color: '#F59E0B',
    matches: ['near miss', 'environmental incident', 'property damage', 'report only'],
  },
  { key: 'unclassified', label: 'Unclassified', color: '#9CA3AF', matches: [] },
];

const TIER_BY_SEVERITY = SEVERITY_TIERS.reduce((acc, tier) => {
  tier.matches.forEach((m) => {
    acc[m] = tier.key;
  });
  return acc;
}, {});

export const tierOfSeverity = (hint) => {
  const key = String(hint || '').trim().toLowerCase();
  return TIER_BY_SEVERITY[key] || 'unclassified';
};

// How the top 10 is chosen. `valueOf` returns the number the bars are drawn and
// sorted by; `format` renders it for labels and tooltips.
export const METRICS = [
  {
    key: 'forms',
    label: 'Total Forms',
    color: '#155DFC',
    blurb: 'Busiest jobs — every safety form recorded against the job.',
    valueOf: (j) => j.total,
    format: (v) => String(v),
  },
  {
    key: 'risk',
    label: 'Safety Risk',
    color: '#DC2626',
    blurb:
      'Composite 0-100 score: BBS concern rate, failed audit items per S&H form and incident count.',
    valueOf: (j) => j.riskScore,
    format: (v) => `${v}/100`,
  },
  {
    key: 'incidents',
    label: 'Incidents',
    color: '#E7000B',
    blurb: 'Jobs with the most accident / incident reports filed.',
    valueOf: (j) => j.incidents,
    format: (v) => String(v),
  },
  {
    key: 'concern',
    label: 'BBS Concern Rate',
    color: '#D97706',
    blurb:
      'Share of BBS observations marked Concern. Jobs with fewer than 5 observations are excluded so single observations cannot reach 100%.',
    valueOf: (j) => j.concernRate,
    format: (v) => `${v}%`,
  },
];

export const MIN_BBS_OBSERVATIONS = 5;
export const TOP_N = 10;
// Ten lines on one time axis is unreadable; the trend shows the leading few.
export const TREND_SERIES = 5;

// Job labels arrive as "Fab Shop Job Expenses-(9990)"; drop the trailing number
// so the same job does not split on formatting. Matches correlation.jsx.
export const normalizeJobName = (s) =>
  String(s || '').replace(/-\(\d+\)\s*$/, '').trim();

// Same weighting as the Safety Analytics overall risk score, applied per job so
// the number means the same thing wherever it appears.
export const riskScoreOf = ({ concernRate, failedAudits, shpForms, incidents }) => {
  const cConcern = Math.min(60, concernRate * 2);
  const cFailed = Math.min(25, (failedAudits / Math.max(1, shpForms)) * 25);
  const cIncident = Math.min(15, incidents * 3);
  return Math.round(cConcern + cFailed + cIncident);
};

// Counts Safe / Concern observations on a BBS row.
export const tallyBbs = (row) => {
  let safe = 0;
  let concern = 0;
  const meta = row?.bbs_meta;
  if (meta && typeof meta === 'object') {
    Object.values(meta).forEach((arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((it) => {
        if (it?.answer === 'S') safe += 1;
        else if (it?.answer === 'C') concern += 1;
      });
    });
  }
  return { safe, concern };
};

// "2026-09-18" / "2026-09-18T10:00:00" -> "2026-09". filled_date is the date the
// observer wrote on the form, so it is preferred over the row's insert time.
const monthOf = (row) => {
  const raw = row?.filled_date || row?.created_at;
  if (!raw) return null;
  const m = String(raw).slice(0, 7);
  return /^\d{4}-\d{2}$/.test(m) ? m : null;
};

// Folds the feed into one entry per job.
export const rollupByJob = (rows) => {
  const jobs = {};

  (Array.isArray(rows) ? rows : []).forEach((r) => {
    const jobName = normalizeJobName(r.jobDescription);
    const jobId = r.job_id && Number(r.job_id) > 0 ? String(r.job_id) : null;
    const companyid = r.companyid != null ? String(r.companyid) : '';
    // Unnumbered rows cannot be opened as a job panel, but still belong to a
    // real job by name, so they are grouped rather than dropped. Job numbers
    // repeat across companies, so the company is part of the identity.
    const key = `${companyid}:${jobId || jobName || 'Unknown'}`;
    if (!jobs[key]) {
      jobs[key] = {
        key,
        jobId,
        jobName,
        companyid,
        companyName: r.company_name || '',
        label: jobId ? `${jobId} - ${jobName || 'Unnamed'}` : jobName || 'Unknown',
        total: 0,
        safe: 0,
        concern: 0,
        failedAudits: 0,
        shpForms: 0,
        incidents: 0,
        byMonth: {},
        ...Object.fromEntries(FORM_CODES.map((c) => [c, 0])),
        ...Object.fromEntries(SEVERITY_TIERS.map((t) => [t.key, 0])),
      };
    }
    const j = jobs[key];
    if (!j.companyName && r.company_name) j.companyName = r.company_name;
    const code = String(r.form_type || '').toUpperCase();

    j.total += 1;
    if (FORM_CODES.includes(code)) j[code] += 1;

    const month = monthOf(r);
    if (month) j.byMonth[month] = (j.byMonth[month] || 0) + 1;

    if (code === 'BBS') {
      const { safe, concern } = tallyBbs(r);
      j.safe += safe;
      j.concern += concern;
    } else if (code === 'SHP' || code === 'SHPA') {
      j.shpForms += 1;
      j.failedAudits += Number(r?.shp_meta?.infractions_count) || 0;
    } else if (code === 'AIF') {
      j.incidents += 1;
      j[tierOfSeverity(r.severity_hint)] += 1;
    }
  });

  return Object.values(jobs).map((j) => {
    const observations = j.safe + j.concern;
    const concernRate = observations
      ? +((j.concern / observations) * 100).toFixed(1)
      : 0;
    return {
      ...j,
      observations,
      concernRate,
      // Failed audit items per S&H form — a raw count just re-ranks by how many
      // audits a job happened to have.
      auditFailRate: j.shpForms
        ? +(j.failedAudits / j.shpForms).toFixed(1)
        : 0,
      riskScore: riskScoreOf({
        concernRate,
        failedAudits: j.failedAudits,
        shpForms: j.shpForms,
        incidents: j.incidents,
      }),
    };
  });
};

// Picks the top N jobs for a metric, most-significant first.
export const rankJobs = (jobs, metric, limit = TOP_N) => {
  let pool = Array.isArray(jobs) ? jobs : [];
  // A job with one Concern observation is 100% concern; that is noise, not a
  // ranking, so require a usable sample before it can top the chart.
  if (metric.key === 'concern') {
    pool = pool.filter((j) => j.observations >= MIN_BBS_OBSERVATIONS);
  }
  return pool
    .filter((j) => metric.valueOf(j) > 0)
    .sort((a, b) => metric.valueOf(b) - metric.valueOf(a) || b.total - a.total)
    .slice(0, limit);
};

// Wide-format monthly activity for the trend chart: one row per month, one
// column per job. Months with no forms anywhere are absent rather than zero, so
// the axis only spans periods that actually have data.
export const buildTrendSeries = (jobs, limit = TREND_SERIES) => {
  const series = jobs.slice(0, limit);
  const months = new Set();
  series.forEach((j) => Object.keys(j.byMonth).forEach((m) => months.add(m)));

  const data = [...months]
    .sort()
    .map((month) => {
      const point = { month };
      series.forEach((j) => {
        point[j.key] = j.byMonth[month] || 0;
      });
      return point;
    });

  return { data, series };
};
